import { sidebarConfig } from "@backstage/core-components";
import {
	createUnifiedTheme,
	genPageTheme,
	palettes,
	type PageTheme,
	type UnifiedThemeOptions,
} from "@backstage/theme";

// Implement Consulting Group brand tokens. Lead pair (deliberately chosen over the brand doc's
// Blue-Green/Green-Grey default, to match the maroon accent already used across IM's internal tools,
// e.g. IM GPT): Deep Red/Wine + Brown. Blue-Green is reserved for links only, per the brand doc's
// "no colored text (body is always neutral; links may be Blue-Green)" rule.
const deepRed = "#451F23";
const brown = "#9A6E44";
const yellow = "#E4B73C";
const piggy = "#E1C1B5";
const blueGreen = "#67817F";
const anthracite = "#1F1F23";
const ash = "#30373B";
const grey = "#A8A5A1";
const lightGrey = "#F2F2F1";
const border = "#E1E6E5";
// The light theme's sidebar panel — a hair off-white, so it reads as a distinct surface against the
// now-white content area without needing a heavy fill. IM GPT's own sidebar treatment.
const navSurface = "#F7F7F6";

// The sidebar items' side margin, in px.
const INSET = 8;

const fontFamily = "Arial, Helvetica, sans-serif";

// ─────────────────────────────────────────────────────────────────────────────────────────────────
// This file follows IM GPT's softer reading of the brand system (light nav, pill CTAs, ~12px radius,
// flat headers, bold-sans hero) rather than the brand doc's literal component defaults, so the two
// internal tools feel like one product family. Colors are unaffected — only shape, weight, polarity.
//
// So the 2px / uppercase / serif brand-doc defaults are absent on purpose — don't "restore" them.
// ─────────────────────────────────────────────────────────────────────────────────────────────────

// The nav palette is per-theme, not shared: the light sidebar is near-white with anthracite text and
// the dark one is the inverse, so one object cannot serve both.
const lightNavigation = {
	background: navSurface,
	color: anthracite,
	indicator: deepRed,
	selectedColor: deepRed,
	navItem: {
		// A neutral wash rather than a colored fill — Deep Red shouts on a light panel.
		hoverBackground: lightGrey,
	},
	submenu: {
		background: lightGrey,
	},
};

const darkNavigation = {
	background: anthracite,
	color: "#F8F5E7",
	indicator: yellow,
	selectedColor: "#FFFFFF",
	navItem: {
		hoverBackground: deepRed,
	},
	submenu: {
		background: ash,
	},
};

// FLAT PAGE HEADERS. `shape: "none"` plus a single color collapses genPageTheme's
// `${shape}, ${gradient}` backgroundImage to a flat fill; `options.fontColor` is what <Header> reads.
// All four page-theme keys share one theme — the per-key color-coding is Backstage's convention, not
// Implement's. The header gets its edge from the BackstageHeader override below.
const flatPageTheme = (background: string, fontColor: string): Record<string, PageTheme> => {
	const theme = genPageTheme({
		colors: [background],
		shape: "none",
		options: { fontColor },
	});
	return { home: theme, documentation: theme, tool: theme, other: theme };
};

// Radius 12px on surfaces and fully-rounded pill buttons in sentence case — IM GPT's shape language.
// Thin neutral borders and a single soft shadow do the separating work, which is what lets the
// content area go pure white without cards dissolving into it.
//
// Both themes share this one object, so anything theme-dependent MUST be a function of `theme`, never
// a literal — a hardcoded light-mode assumption here is invisible text in dark mode. Function-valued
// styleOverrides work because `adaptV5Override` calls `style({ theme })`.
const components: UnifiedThemeOptions["components"] = {
	MuiButton: {
		styleOverrides: {
			root: {
				borderRadius: 999,
				textTransform: "none" as const,
				fontWeight: 600,
				padding: "0.65rem 1.5rem",
			},
			outlined: {
				borderWidth: 1,
			},
		},
	},
	MuiPaper: {
		styleOverrides: {
			rounded: {
				borderRadius: 12,
			},
		},
	},
	MuiCard: {
		styleOverrides: {
			root: ({ theme }) => ({
				borderRadius: 12,
				border: `1px solid ${theme.palette.divider}`,
				boxShadow: "0 1px 2px rgba(31,31,35,0.04)",
			}),
		},
	},
	MuiLink: {
		styleOverrides: {
			// Body text stays neutral; links are the one place brand allows color, and it's always
			// Blue-Green, independent of whichever pair is chosen as the interactive lead.
			root: {
				color: blueGreen,
			},
		},
	},

	// ── Backstage's own v4 stylesheets ───────────────────────────────────────────────────────────
	// `createUnifiedTheme({ components })` transforms EVERY key generically into a MUI v4
	// `theme.overrides[name]` entry (unified/overrides.esm.js), and core-components registers each of
	// these under a `name:` — so `Backstage*` keys are a supported styling surface, not a hack. This
	// is the only way to reach values core-components hardcodes.

	// The sidebar reads the pages' --bui-* tokens, not theme.palette. Rules: docs/app/frontend-system.md § Brand tokens for BUI.
	BackstageSidebar: {
		styleOverrides: {
			drawer: ({ theme }) => ({
				borderRight: "1px solid var(--bui-border-1)",
				// Dividers are styled here, not via BackstageSidebarDivider: that override renders nothing (see the doc).
				"& hr": {
					height: 1,
					width: "100%",
					border: "none",
					margin: theme.spacing(1.2, 0),
					background: "var(--bui-border-1)",
				},
			}),
		},
	},

	// Everything after <SidebarSpace> is the utility block (Notifications, Feedback, commit, Settings).
	BackstageSidebarSpace: {
		styleOverrides: {
			root: {
				"& ~ a, & ~ button, & ~ * a, & ~ * button": { color: "var(--bui-fg-secondary)" },
				// Secondary text on the hover fill is under 4.5:1 in dark, so hover steps up.
				"& ~ a:hover, & ~ button:hover, & ~ * a:hover, & ~ * button:hover": {
					color: "var(--bui-fg-primary)",
				},
			},
		},
	},

	// An inset rounded highlight (8px margin, --bui-radius-2) replaces the full-width row and the
	// left indicator bar. Widths shrink by the margins so icons stay centred in their container.
	BackstageSidebarItem: {
		styleOverrides: {
			root: {
				margin: `0 ${INSET}px`,
				borderRadius: "var(--bui-radius-2)",
			},
			// buttonItem (Search, the commit indicator) resets `margin: 0` after `root`, and MUI Button sets min-width 64.
			buttonItem: { margin: `0 ${INSET}px`, minWidth: 0 },
			open: {
				"@media (min-width: 600px)": { width: sidebarConfig.drawerWidthOpen - 2 * INSET },
			},
			closed: { width: sidebarConfig.drawerWidthClosed - 2 * INSET },
			iconContainer: { width: sidebarConfig.iconContainerWidth - 2 * INSET },
			highlightable: {
				"&:hover": { background: "var(--bui-bg-neutral-1-hover)" },
			},
			highlighted: { background: "var(--bui-bg-neutral-1-hover)" },
			selected: {
				"&$root": {
					borderLeft: "none",
					background: "var(--bui-bg-neutral-1-pressed)",
					color: "var(--bui-fg-primary)",
				},
				"&$closed": { width: sidebarConfig.drawerWidthClosed - 2 * INSET },
				"& $closedItemIcon": { paddingRight: 0 },
				"& $iconContainer": { marginLeft: 0 },
				"& $label": { fontWeight: "var(--bui-font-weight-bold)" },
			},
			// core-components sets unselected labels to `fontWeight: "bold"`; IM GPT's are medium.
			label: {
				fontWeight: 500,
			},
			// And the search placeholder to a hardcoded "#b5b5b5", barely legible on a near-white panel.
			searchField: {
				color: grey,
			},
		},
	},

	// <Page> is the scroll container; reserve its scrollbar's width so centred content doesn't jump
	// sideways when a page grows or shrinks past one screen (e.g. filtering a table).
	BackstagePage: {
		styleOverrides: {
			root: {
				scrollbarGutter: "stable",
			},
		},
	},

	// A flat header has no gradient to separate it from content, so it needs an explicit edge.
	BackstageHeader: {
		styleOverrides: {
			header: ({ theme }) => ({
				borderBottom: `1px solid ${theme.palette.divider}`,
				boxShadow: "none",
			}),
		},
	},

	BackstageInfoCard: {
		styleOverrides: {
			header: {
				padding: "1.25rem 1.5rem 0.75rem",
			},
		},
	},
};

export const companyLightTheme = createUnifiedTheme({
	fontFamily,
	palette: {
		...palettes.light,
		primary: { main: deepRed, light: piggy, dark: anthracite },
		secondary: { main: brown, light: yellow, dark: blueGreen },
		// White content area with the sidebar as the tinted panel, matching IM GPT. Cards stay white
		// and separate via their border rather than a background contrast.
		background: { default: "#FFFFFF", paper: "#FFFFFF" },
		text: { primary: anthracite, secondary: ash },
		// `divider` is read by the Backstage* overrides above rather than each hardcoding `border`,
		// which is what keeps one shared `components` object correct in both themes.
		divider: border,
		// Backstage's own <Link> (core-components) reads palette.link/linkHover directly, rather
		// than the generic MuiLink override below — both are needed to cover Backstage links AND
		// plain MUI Links everywhere else, per the brand doc's "links may be Blue-Green" rule.
		link: blueGreen,
		linkHover: ash,
		navigation: lightNavigation,
	},
	defaultPageTheme: "home",
	pageTheme: flatPageTheme("#FFFFFF", anthracite),
	components,
});

export const companyDarkTheme = createUnifiedTheme({
	fontFamily,
	palette: {
		...palettes.dark,
		// "Brand colors unchanged" in dark mode, per the brand doc — so the lead stays Deep
		// Red/Brown rather than swapping to an unrelated dark-mode accent. Piggy (a Deep-Red-family
		// tint) stands in as the interactive `main` for contrast against a dark background; the
		// literal Deep Red is kept as the `dark` shade instead.
		primary: { main: piggy, light: "#F1DAD2", dark: deepRed },
		secondary: { main: brown, light: yellow, dark: deepRed },
		background: { default: anthracite, paper: ash },
		text: { primary: lightGrey, secondary: grey },
		// The dark counterpart of the light theme's `border` — a hairline that lifts off anthracite
		// instead of one that sits on white.
		divider: "#3A4045",
		link: blueGreen,
		linkHover: "#A4B3B2", // Blue-Green's 60% tint, lightening on hover against the dark background
		navigation: darkNavigation,
	},
	defaultPageTheme: "home",
	pageTheme: flatPageTheme(ash, lightGrey),
	components,
});
